import {
  Container,
  Heading,
  Text,
  VStack,
  Box,
  SimpleGrid,
  Flex,
  Icon,
  HStack,
  Button,
  useColorModeValue,
  Grid,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  Users,
  Compass,
  MapPin,
  Database,
  GlobeHemisphereWest,
  ArrowRight,
} from '@phosphor-icons/react';

export const MissionSection = () => {
  // Use theme tokens directly
  const headingColor = useColorModeValue('interactive.primary', 'interactive.primary');
  const textColor = useColorModeValue('text.default', 'text.default');
  const iconBg = useColorModeValue('bg.accent', 'bg.accent');
  const iconColor = useColorModeValue('interactive.primary', 'interactive.primary');
  const sectionBg = useColorModeValue('bg.default', 'bg.default'); // Use default page background

  const targetUsers = [
    { icon: Users, text: "Researchers and academics" },
    { icon: Compass, text: "History enthusiasts" },
    { icon: MapPin, text: "Site visitors and tourists" },
    { icon: Database, text: "Local historians and archaeologists" },
    { icon: GlobeHemisphereWest, text: "Cultural preservation organizations" }
  ];

  return (
    <Box
      bg={sectionBg}
      py={{ base: 12, md: 16 }}
      position="relative" // Needed for pseudo-element positioning
      _before={{ // Add background texture overlay
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.05, 0.08), // Subtle opacity for default background
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '500px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack
          spacing={{ base: 8, md: 10 }}
          align={{ base: "center", lg: "flex-start" }}
          textAlign={{ base: "center", lg: "left" }}
          alignItems={{ base: "center", lg: "flex-start" }}
        >
          {/* Mission Header with Decorative Elements */}
          <VStack spacing={4} align={{ base: "center", lg: "flex-start" }}>
            <HStack spacing={3} align="center">
              <Icon as={Compass} boxSize={8} color={headingColor} />
              <Heading 
                as="h2" 
                size="2xl" 
                color={headingColor} 
                fontFamily="accent"
                letterSpacing="wide"
              >
                Our Mission
              </Heading>
            </HStack>
            
            <Divider 
              orientation="horizontal" 
              borderColor={useColorModeValue('border.default', 'border.default')}
              opacity={0.6}
              w={{ base: "200px", lg: "300px" }}
            />
          </VStack>

          {/* Mission Statement in Journal Style */}
          <Box
            bg={useColorModeValue('bg.surface', 'bg.surface')}
            p={{ base: 6, md: 8 }}
            borderRadius="lg"
            border="2px solid"
            borderColor={useColorModeValue('border.light', 'border.light')}
            boxShadow="md"
            position="relative"
            maxW="4xl"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              opacity: useColorModeValue(0.08, 0.12),
              pointerEvents: 'none',
              bgImage: 'var(--texture-parchment)',
              bgRepeat: 'repeat',
              bgSize: '300px',
              bgBlendMode: useColorModeValue('soft-light', 'multiply'),
              borderRadius: 'inherit',
              zIndex: 0,
            }}
          >
            <VStack spacing={4} position="relative" zIndex={1}>
              <Text 
                fontSize={{ base: 'lg', md: 'xl' }} 
                fontFamily="body" 
                color={textColor}
                lineHeight="tall"
              >
                To create a comprehensive and accessible resource for anyone interested in megalithic structures, 
                fostering collaboration and discovery within the community. We aim to centralize scattered 
                information about these ancient sites and provide powerful tools for exploration, documentation, 
                and knowledge sharing.
              </Text>
            </VStack>
          </Box>

          {/* Target Users Section */}
          <VStack spacing={6} align={{ base: "center", lg: "flex-start" }} w="full">
            <HStack spacing={3} align="center">
              <Icon as={Users} boxSize={7} color={headingColor} />
              <Heading 
                as="h3" 
                size="lg" 
                color={textColor} 
                fontFamily="heading"
              >
                Target Users
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width="full">
              {targetUsers.map((item, index) => (
                <Box
                  key={index}
                  bg={useColorModeValue('bg.surface', 'bg.surface')}
                  p={4}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={useColorModeValue('border.light', 'border.light')}
                  transition="all 0.3s ease"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                    borderColor: useColorModeValue('border.default', 'border.default'),
                  }}
                >
                  <HStack spacing={4} align="center">
                    <Flex
                      w="50px"
                      h="50px"
                      bg={iconBg}
                      color={iconColor}
                      borderRadius="full"
                      justify="center"
                      align="center"
                      boxShadow="sm"
                      border="2px solid"
                      borderColor={useColorModeValue('border.light', 'border.light')}
                    >
                      <Icon as={item.icon} boxSize={6} weight="duotone" />
                    </Flex>
                    <Text 
                      fontFamily="heading" 
                      color={textColor}
                      fontWeight="medium"
                      fontSize="md"
                      flex={1}
                    >
                      {item.text}
                    </Text>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>

          {/* Call to Action */}
          <Box pt={4}>
            <Button
              as={Link}
              to="/map"
              size="lg"
              variant="journal"
              rightIcon={<Icon as={ArrowRight} weight="bold" />}
              fontFamily="heading"
              letterSpacing="wide"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
            >
              Explore Our Map
            </Button>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};